import type { Contraction, HospitalRule, HospitalRuleConfig } from '../types';
import { HOSPITAL_RULES } from '../types';

/**
 * Get the hospital rule configuration by name
 */
export function getHospitalRuleConfig(rule: HospitalRule): HospitalRuleConfig {
  return HOSPITAL_RULES.find((r) => r.name === rule) || HOSPITAL_RULES[0];
}

/**
 * Check if the hospital rule is met based on contractions
 *
 * A rule like "5-1-1" means:
 * - Contractions every 5 minutes or less (interval)
 * - Each contraction lasts at least 1 minute (duration)
 * - This pattern has been going on for at least 1 hour
 */
export function checkHospitalRule(
  contractions: Contraction[],
  rule: HospitalRule
): { isMet: boolean; details: string } {
  const ruleConfig = getHospitalRuleConfig(rule);
  const { intervalMinutes, durationMinutes, hours } = ruleConfig;

  // Get completed contractions sorted by start time (newest first)
  const completedContractions = contractions
    .filter((c) => c.end_time !== null && c.duration_seconds !== null)
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

  if (completedContractions.length < 3) {
    return { isMet: false, details: 'Noch nicht genug Wehen erfasst' };
  }

  // Check contractions in the last hour
  const oneHourAgo = Date.now() - hours * 60 * 60 * 1000;
  const recentContractions = completedContractions.filter(
    (c) => new Date(c.start_time).getTime() >= oneHourAgo
  );

  if (recentContractions.length < 3) {
    return { isMet: false, details: 'Zu wenige Wehen in der letzten Stunde' };
  }

  // Calculate average interval and duration for recent contractions
  const intervals = recentContractions
    .map((c) => c.interval_seconds)
    .filter((i): i is number => i !== null);

  const durations = recentContractions
    .map((c) => c.duration_seconds)
    .filter((d): d is number => d !== null);

  if (intervals.length < 2 || durations.length < 2) {
    return { isMet: false, details: 'Noch nicht genug Daten' };
  }

  const avgIntervalMinutes = intervals.reduce((a, b) => a + b, 0) / intervals.length / 60;
  const avgDurationSeconds = durations.reduce((a, b) => a + b, 0) / durations.length;

  // Expected minimum contractions per hour based on interval
  const expectedContractionsPerHour = Math.floor(60 / intervalMinutes);

  // Check all conditions
  const intervalMet = avgIntervalMinutes <= intervalMinutes;
  const durationMet = avgDurationSeconds >= durationMinutes * 60;
  const countMet = recentContractions.length >= expectedContractionsPerHour;

  if (intervalMet && durationMet && countMet) {
    return {
      isMet: true,
      details: `${rule} Regel erfüllt: Ø ${avgIntervalMinutes.toFixed(1)} Min Abstand, Ø ${Math.round(avgDurationSeconds)} Sek Dauer`,
    };
  }

  // Provide details on what's missing
  const missing: string[] = [];
  if (!intervalMet) {
    missing.push(`Abstände noch >${intervalMinutes} Min`);
  }
  if (!durationMet) {
    missing.push(`Dauer noch <${durationMinutes} Min`);
  }
  if (!countMet) {
    missing.push(`Nur ${recentContractions.length}/${expectedContractionsPerHour} Wehen/Stunde`);
  }

  return { isMet: false, details: missing.join(', ') };
}

/**
 * Get a human-readable description of the hospital rule
 */
export function getHospitalRuleDescription(rule: HospitalRule): string {
  const config = getHospitalRuleConfig(rule);
  return config.description;
}
